import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import apiClient from "utils/apiClient";
import { PAYMENT_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${PAYMENT_SERVICE_BASE_URL}/api/v1/payments`;

export default function usePaymentTransactions() {
  const authToken = sessionStorage.getItem("authToken");

  const [paymentTransactions, setPaymentTransactions] = useState([]);
  const [paymentTransactionsMeta, setPaymentTransactionsMeta] = useState({
    page: 0,
    number: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";

    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message;
    }

    toast.error(message);
    console.error(`${action} error`, err);
  };

  const fetchPaymentTransactions = useCallback(
    async ({ start, end, page = 0, size = 20, chargeItemCode } = {}) => {
      if (!authToken) return;

      const startDate = start || dayjs().startOf("month").toISOString();
      const endDate = end || dayjs().endOf("day").toISOString();

      setLoading(true);
      setError(null);

      try {
        const params = {
          start: startDate,
          end: endDate,
          page,
          size,
        };
        if (
          chargeItemCode !== undefined &&
          chargeItemCode !== null &&
          String(chargeItemCode).trim() !== ""
        ) {
          params.chargeItemCode = String(chargeItemCode).trim();
        }

        const res = await apiClient.get(`${BASE_URL}/charges`, { params });
        const payload = res.data || {};

        const content = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.content)
            ? payload.content
            : [];

        const meta = Array.isArray(payload)
          ? {
              page,
              number: page,
              size,
              totalElements: content.length,
              totalPages: 1,
            }
          : {
              ...payload,
              page: payload.page ?? payload.number ?? page,
              number: payload.number ?? payload.page ?? page,
              size: payload.size ?? size,
              totalElements: payload.totalElements ?? content.length,
              totalPages: payload.totalPages ?? 1,
            };

        setPaymentTransactions(content);
        setPaymentTransactionsMeta(meta);

        return { ...meta, content };
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "fetch payment transactions");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  return {
    paymentTransactions,
    paymentTransactionsMeta,
    fetchPaymentTransactions,
    loading,
    error,
  };
}
